import { useEffect } from 'react';
import { Modal } from 'antd';
import { useStore } from '@/stores';
import type { MerchantContextStore } from '@/stores/MerchantContextStore';

// Changing practice is a context transition: pages holding one practice's entity clear it and load again for the new
// one (AgentDetailStore, AgentRegistryStore). Every place that switches practice goes through `switchPractice`, so
// unsaved edits are never dropped without asking; editors report their unsaved state with `useHoldUnsavedWork`.

/**
 * Switches the selected practice. When edits are unsaved, asks first; `onSwitched` runs only once the switch happens.
 * Returns false when nothing changed (same practice, or the person kept editing).
 */
export function switchPractice(ctx: MerchantContextStore, merchantId: number, onSwitched?: () => void): Promise<boolean> {
  if (merchantId === ctx.selectedMerchantId) {
    onSwitched?.();
    return Promise.resolve(false);
  }
  const go = (): boolean => {
    ctx.selectMerchant(merchantId);
    onSwitched?.();
    return true;
  };
  const work = ctx.unsavedWork;
  if (work.length === 0) return Promise.resolve(go());
  const target = ctx.merchants.find((m) => m.id === merchantId)?.title ?? `practice #${merchantId}`;
  return new Promise((resolve) => {
    Modal.confirm({
      title: `Switch to ${target} and discard unsaved changes?`,
      content: `Not saved yet: ${work.join('; ')}. Switching practice closes them.`,
      okText: 'Discard and switch',
      okButtonProps: { danger: true },
      cancelText: 'Keep editing',
      onOk: () => resolve(go()),
      onCancel: () => resolve(false),
    });
  });
}

/** Holds `label` as unsaved work while `dirty` is true, so a practice switch asks before discarding it. */
export function useHoldUnsavedWork(dirty: boolean, label: string): void {
  const { merchantContextStore: ctx } = useStore();
  useEffect(() => (dirty ? ctx.holdUnsaved(label) : undefined), [ctx, dirty, label]);
}
